import React, { useEffect, useState, useRef } from "react";
import Header from "./frameTop";
import Sidebar from "./frameLeft";
import FrameMdi from "./frameMdi";
import { useSelector, useDispatch } from "react-redux";
import { setMenuId, setMenuData, setMyMenuData, setAddBtnData } from "../store/userSlice";
import DynamicContent from "./frameWork";
import { useApiUtil } from "../utils/apiUtil";
import { openOrderHistoryWindow } from "../utils/orderHistoryWindow";

const MainPage = () => {
  // 📌 Context에서 권한 정보 및 전역 menuId setter 가져오기
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const permId = useSelector((state) => state.user.permId); // 읽기
  const dispatch = useDispatch();

  const menuData = useSelector((state) => state.user.menuData);
  const addBtnData = useSelector((state) => state.user.addBtnData);

  // 📌 상태값 정의
  const [headerMenuData, setHeaderMenuData] = useState([]);
  const [sidebarMenuData, setSidebarMenuData] = useState([]);
  const [selectedMenu, setSelectedMenu] = useState(null);

  const [tabs, setTabs] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [activeTabHistory, setActiveTabHistory] = useState([]);

  const tabComponentCache = useRef({});

  const { gfnCallApi } = useApiUtil();

  // ───────────────────────────────────────
  // ✅ 최초 진입 시: 헤더 메뉴 로딩
  useEffect(() => {
    const fetchHeaderMenu = async () => {
      try {
        const res = await gfnCallApi({
          url: "menu",
          method: "POST",
          data: {
            permId: permId,
          },
          showMsg: false,
        });
        if (res.data?.menu?.length > 0) {
          dispatch(setMenuData(res.data.menu));
          dispatch(setMyMenuData(res.data.mymenu));
          dispatch(setAddBtnData(res.data.addBtn));

          const rootMenus = res.data.menu.filter(item => item.MENU_LV === 0);
          setHeaderMenuData(rootMenus);
          setSelectedMenu(rootMenus[0]); // 첫 메뉴 자동 선택
        }
      } catch (err) {
        console.error("헤더 메뉴 조회 실패:", err);
      }
    };

    fetchHeaderMenu();
  }, [permId]);

  // ───────────────────────────────────────
  // ✅ 헤더 메뉴 선택 시: 사이드 메뉴 로딩
  useEffect(() => {
    const fetchSidebarMenu = async () => {
      if (!selectedMenu) return;

      const moduleCd = selectedMenu.MODULE_CD;
      const moduleMenu = menuData.filter(item => item.MODULE_CD === moduleCd && item.MENU_LV > 0);
      setSidebarMenuData(moduleMenu);
    };

    fetchSidebarMenu();
  }, [selectedMenu, permId]);


  useEffect(() => {
    const handler = (e) => {
      const menuId = e.state?.menuId;
      if (menuId) setActiveTab(menuId);
      else setGoHome(true);// setActiveTab(null); // 홈 등
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);


  // ───────────────────────────────────────
  // ✅ 사이드 메뉴 클릭 시: 탭 추가 및 전환
  const handleSidebarClick = (
    menuId,
    menuPathTitles,
    prgmId,
    permId,
    sytmFlagCd,
    prgmPath,
    printBtnAuth,
    addBtnAuth,
    delBtnAuth,
    saveBtnAuth,
    excelBtnAuth,
    selectBtnAuth,
    parm,
    selectedData = null,
    addBtnList
  ) => {
    dispatch(setMenuId(menuId)); // 값 설정

    if (prgmPath?.includes("searchOrderHistory")) {
      openOrderHistoryWindow();
      return;
    }

    const menuName = menuPathTitles.at(-1);
    const fullPath = menuPathTitles.join(" > ");
    const permissions = {
      menuId,
      prgmId,
      permId,
      sytmFlagCd,
      print: printBtnAuth === "1",
      add: addBtnAuth === "1",
      del: delBtnAuth === "1",
      save: saveBtnAuth === "1",
      excel: excelBtnAuth === "1",
      select: selectBtnAuth === "1",
    };
    setTabs((prevTabs) => {

      // 여기서 prevTabs는 항상 최신 상태!
      const tabIndex = prevTabs.findIndex(tab => tab.menuId === menuId);
      if (tabIndex === -1) {
        // 신규 탭 추가
        return [...prevTabs, { menuId,
          menuName, fullPath, prgmId, prgmPath, parm, permissions, selectedData, addBtnList 
        }];
      } else {
        // 기존 탭 데이터 업데이트
        return prevTabs.map((tab, idx) => idx === tabIndex ? { ...tab, selectedData } : tab);
      }
    });

  window.history.pushState({menuId}, '', '');
  setActiveTab(menuId);
};

const openLoadUnloadManagement = () => {
  const targetMenu = menuData.find((menu) =>
    menu.PRGM_PATH?.replace(/\\/g, "/").includes("basic/loadUnloadMng/loadUnloadMng")
  );
  if (!targetMenu) return false;

  handleSidebarClick(
    targetMenu.MENU_ID,
    targetMenu.DISPLAY_PATH.split(" > "),
    targetMenu.PRGM_ID,
    targetMenu.PERM_ID,
    targetMenu.SYTM_FLAG_CD,
    targetMenu.PRGM_PATH,
    targetMenu.CMMNBTNPRINT,
    targetMenu.CMMNBTNADD,
    targetMenu.CMMNBTNDEL,
    targetMenu.CMMNBTNSAVE,
    targetMenu.CMMNBTNEXCELDOWN,
    targetMenu.CMMNBTNSEARCH,
    targetMenu.PARM,
    null,
    addBtnData.filter((item) => item.MENU_ID === targetMenu.MENU_ID)
  );
  return true;
};

const openVehicleManagement = () => {
  const targetMenu = menuData.find((menu) =>
    menu.PRGM_PATH?.replace(/\\/g, "/").includes("dispatch/vehicle/Vehicle")
  );
  if (!targetMenu) return false;

  handleSidebarClick(
    targetMenu.MENU_ID,
    targetMenu.DISPLAY_PATH.split(" > "),
    targetMenu.PRGM_ID,
    targetMenu.PERM_ID,
    targetMenu.SYTM_FLAG_CD,
    targetMenu.PRGM_PATH,
    targetMenu.CMMNBTNPRINT,
    targetMenu.CMMNBTNADD,
    targetMenu.CMMNBTNDEL,
    targetMenu.CMMNBTNSAVE,
    targetMenu.CMMNBTNEXCELDOWN,
    targetMenu.CMMNBTNSEARCH,
    targetMenu.PARM,
    null,
    addBtnData.filter((item) => item.MENU_ID === targetMenu.MENU_ID)
  );
  return true;
};

const renderTabContent = (tab) => {

  if (!tabComponentCache.current[tab.menuId]) {
    tabComponentCache.current[tab.menuId] = {
      ref: React.createRef(),
    };
  }
  const { ref } = tabComponentCache.current[tab.menuId];

  const component = (
    <DynamicContent
      ref={ref}
      menuId={tab.menuId}
      prgmId={tab.prgmId}
      parm={tab.parm}
      prgmPath={tab.prgmPath}
      permissions={tab.permissions}
      onCloseTab={() => handleCloseTab(tab.menuId)}
      onOpenLoadUnloadManagement={openLoadUnloadManagement}
      onOpenVehicleManagement={openVehicleManagement}
    />
  );

  return component;
};

const handleTabClick = (menuId) => {
  setActiveTabHistory(prev => 
    prev.length === 0 || prev[prev.length - 1] !== menuId
      ? [...prev, menuId]
      : prev
  );

  window.history.pushState({menuId}, '', '');
  setActiveTab(menuId);
};

// ───────────────────────────────────────
// ✅ 탭 닫기 핸들러

// 탭이 다 닫혔을 때, Home으로 돌아가기 위함
const [goHome, setGoHome] = useState(false);

useEffect(()=>{
if(tabs.length === 0) {
    //탭이 다 닫힘
    setGoHome(true);
  }else{
    //탭이 1개라도 있음
    setGoHome(false);
  }
}, [tabs.length]);

const handleCloseTab = (menuId) => {
  setTabs((prev) => prev.filter((tab) => tab.menuId !== menuId));
  if (activeTab === menuId) {
    const remaining = tabs.filter((tab) => tab.menuId !== menuId);
    setActiveTab(remaining.at(-1)?.menuId || null);
  }
};

  // ───────────────────────────────────────
  // ✅ UI 렌더링
  return (
    <div style={{ display: "flex", height: "100vh", flexDirection: "column" }}>
      {/* 상단 헤더 */}
      <Header
        menuData={headerMenuData}
        selectedMenuId={selectedMenu?.MODULE_CD}
        onMenuClick={setSelectedMenu}
      />

      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        {/* 좌측 사이드바 */}
        {isSidebarOpen && (
          <div style={{ width: "240px", flexShrink: 0 }}>
            <Sidebar
              data={sidebarMenuData}
              menuNm={selectedMenu?.MENU_NM}
              onMenuClick={handleSidebarClick}
            />
          </div>
        )}
        {/* 콘텐츠 MDI 영역 */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            minWidth: 0,
            overflow: "hidden",
            height: "100%",
          }}
        >
          <FrameMdi
            goHome={goHome}
            tabs={tabs}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            openDirectUrl={handleSidebarClick}
            onTabClick={handleTabClick}
            onCloseTab={handleCloseTab}
            renderTabContent={renderTabContent}
            tabComponentCache={tabComponentCache}
            isSidebarOpen={isSidebarOpen}
            setIsSidebarOpen={() => setIsSidebarOpen((prev) => !prev)}
            style={{ height: "100%" }}
          />
        </div>
      </div>
    </div>
  );
};

export default MainPage;
